import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_TIME_FORMAT,
  DEFAULT_TIME_STEP
} from '@treneo/shared/constants';

import {
  DEFAULT_BORDER_SIZE,
  DEFAULT_FONT_SIZE,
  DEFAULT_TAG_COLOR_MODE,
  DEFAULT_TAG_VARIANT,
  DEFAULT_UNIFORM_COLOR
} from '../tags-config';

import { createDefaultPaginationSettings } from './config-ui-persistence';
import type { AppConfigState, PaginationSettings } from './types';

export const selectPaginationSettings = (state: AppConfigState): PaginationSettings =>
  state?.paginationSettings ?? createDefaultPaginationSettings();

export const selectListPaginationSettings =
  (listName: keyof PaginationSettings['lists']) => (state: AppConfigState) => {
    const paginationSettings = selectPaginationSettings(state);
    if (!paginationSettings?.lists) {
      return createDefaultPaginationSettings().lists[listName];
    }
    return paginationSettings.lists[listName];
  };

export const selectCoursesPaginationSettings = (state: AppConfigState) => {
  const paginationSettings = selectPaginationSettings(state);
  if (!paginationSettings?.lists) {
    return createDefaultPaginationSettings().lists.courses;
  }
  return paginationSettings.lists.courses;
};

export const selectCoursesPaginationSettingsFromCombined = (state: {
  config?: AppConfigState;
}): PaginationSettings['lists']['courses'] => {
  const configState = state.config;
  if (!configState?.paginationSettings?.lists) {
    return createDefaultPaginationSettings().lists.courses;
  }
  return configState.paginationSettings.lists.courses;
};

const defaultTagsSettings = () => ({
  variant: DEFAULT_TAG_VARIANT,
  colorMode: DEFAULT_TAG_COLOR_MODE,
  styling: {
    uniformColor: DEFAULT_UNIFORM_COLOR,
    borderSize: DEFAULT_BORDER_SIZE,
    fontSize: DEFAULT_FONT_SIZE
  }
});

export const selectTagsSettings = (state: AppConfigState) =>
  state?.tagsSettings ?? defaultTagsSettings();

export const selectTagVariant = (state: AppConfigState) =>
  selectTagsSettings(state)?.variant ?? DEFAULT_TAG_VARIANT;

export const selectTagColorMode = (state: AppConfigState) =>
  selectTagsSettings(state)?.colorMode ?? DEFAULT_TAG_COLOR_MODE;

export const selectTagStyling = (state: AppConfigState) =>
  selectTagsSettings(state)?.styling ?? {
    uniformColor: DEFAULT_UNIFORM_COLOR,
    borderSize: DEFAULT_BORDER_SIZE,
    fontSize: DEFAULT_FONT_SIZE
  };

const defaultDateTimePickerSettings = () => ({
  dateFormat: DEFAULT_DATE_FORMAT,
  timeFormat: DEFAULT_TIME_FORMAT,
  displayMode: DEFAULT_DISPLAY_MODE,
  timeStep: DEFAULT_TIME_STEP,
  mondayFirst: true
});

export const selectDateTimePickerSettings = (state: AppConfigState) =>
  state?.dateTimePickerSettings ?? defaultDateTimePickerSettings();

export const selectDateTimeDateFormat = (state: AppConfigState) =>
  selectDateTimePickerSettings(state)?.dateFormat ?? DEFAULT_DATE_FORMAT;

export const selectDateTimeTimeFormat = (state: AppConfigState) =>
  selectDateTimePickerSettings(state)?.timeFormat ?? DEFAULT_TIME_FORMAT;

export const selectDateTimeDisplayMode = (state: AppConfigState) =>
  selectDateTimePickerSettings(state)?.displayMode ?? DEFAULT_DISPLAY_MODE;

export const selectDateTimeTimeStep = (state: AppConfigState) =>
  selectDateTimePickerSettings(state)?.timeStep ?? DEFAULT_TIME_STEP;

export const selectDateTimeMondayFirst = (state: AppConfigState) =>
  selectDateTimePickerSettings(state)?.mondayFirst ?? true;
